import "./style.scss";
import React, { useState, useEffect } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import FormApplication from "../FormApplication/FormApplication";
import FormSummary from "../FormSummary/FormSummary";
import { IApplyOnline } from "@/src/types/IApplyOnline";
import FormSuccess from "@/src/components/FormSuccess/FormSuccess";
import axiosInstance from "@/src/api/axiosInstance";

interface IApplyOnlineFormProps {
  onSectionStatusChange: (status: Record<string, string>) => void;
}

const ApplyOnlineForm: React.FC<IApplyOnlineFormProps> = ({ onSectionStatusChange }) => {
  const [stateFormSuccess, setStateFormSuccess] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<IApplyOnline>({
    mode: "onChange",
  });
  const [formData, setFormData] = useState<IApplyOnline>();
  const allFieldsWatch = useWatch({ control });
  const isSubmitDisabled = !isValid;

  const getStatus = (fields: (string | undefined)[]) => {
    const filled = fields.filter((f) => f && f.trim() !== "").length;
    if (filled === fields.length) return "full";
    if (filled > 0) return "not-enough";
    return "";
  };

  const getAllStatusFields = (
    allFields: IApplyOnline,
    overrideFourStatus: string = ""
  ): Record<string, string> => ({
    one: getStatus([allFields.fullname, allFields.phone, allFields.email]),
    two: getStatus([
      allFields.birthDate,
      allFields.nationality,
      allFields.country,
      allFields.address,
    ]),
    three: getStatus([allFields.speciality, allFields.levelEducation, allFields.formStudy]),
    four: overrideFourStatus,
  });

  useEffect(() => {
    const statusFields = getAllStatusFields(allFieldsWatch);
    onSectionStatusChange(statusFields);
  }, [allFieldsWatch, onSectionStatusChange]);

  useEffect(() => {
    setFormData(allFieldsWatch);
  }, [allFieldsWatch]);

  const onSubmit: SubmitHandler<IApplyOnline> = async (data) => {
    const formData = new FormData();

    formData.append("fullname", data.fullname || "");
    formData.append("phone", data.phone || "");
    formData.append("email", data.email || "");

    formData.append("birth_date", data.birthDate || "");
    formData.append("nationality", data.nationality || "");
    formData.append("country", data.country || "");
    formData.append("address", data.address || "");

    formData.append("speciality", data.speciality || "");
    formData.append("level_education", data.levelEducation || "");
    formData.append("form_study", data.formStudy || "");

    formData.append("text", data.comment || "");

    if (data.filePassport?.length) {
      data.filePassport.forEach((file: File) => {
        formData.append("files_passport", file);
      });
    }

    if (data.fileCertificate?.length) {
      data.fileCertificate.forEach((file: File) => {
        formData.append("files_certificate", file);
      });
    }

    if (data.fileEducation?.length) {
      data.fileEducation.forEach((file: File) => {
        formData.append("files_education", file);
      });
    }

    if (data.fileAnouther?.length) {
      data.fileAnouther.forEach((file: File) => {
        formData.append("files_another", file);
      });
    }

    formData.append("form_type", "apply_online");

    try {
      const response = await axiosInstance.post("/form", formData);
      console.log("Успешно отправлено:", response.data);

      const statusFields = getAllStatusFields(data, "full");
      onSectionStatusChange(statusFields);

      setStateFormSuccess(true);
      // reset();
    } catch (error: any) {
      console.error("Ошибка при отправке формы:", error?.response?.data || error.message);
    }
  };

  const [selectedFilesMap, setSelectedFilesMap] = useState<Record<string, File[]>>({
    filePassport: [],
    fileCertificate: [],
    fileEducation: [],
  });

  return (
    <form className="apply-online-form">
      <FormApplication
        register={register}
        control={control}
        errors={errors}
        setValue={setValue}
        handleSubmit={() => handleSubmit(onSubmit)()}
        selectedFilesMap={selectedFilesMap}
        setSelectedFilesMap={setSelectedFilesMap}
        isSubmitDisabled={isSubmitDisabled}
      />
      <FormSummary
        register={register}
        control={control}
        errors={errors}
        setValue={setValue}
        handleSubmit={() => handleSubmit(onSubmit)()}
        selectedFilesMap={selectedFilesMap}
        setSelectedFilesMap={setSelectedFilesMap}
        formData={formData}
        isSubmitDisabled={isSubmitDisabled}
      />
      <FormSuccess stateFormSuccess={stateFormSuccess} setStateFormSuccess={setStateFormSuccess} />
    </form>
  );
};

export default ApplyOnlineForm;
